import { useCallback, useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export const DIALOG = "bg-[#121725] border-white/10 max-w-md sm:rounded-2xl";
export const BTN = "h-10 px-5 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50 inline-flex items-center justify-center gap-2";
export const BTN_GHOST = "h-10 px-5 rounded-full border border-white/15 text-sm hover:bg-white/5 disabled:opacity-50 inline-flex items-center justify-center gap-2";
export const isCancel = (e) => e?.cancelled === true;

function ConfirmIdentityDialog({ open, email, hasPassword, onDone, onCancel }) {
  const [useCode, setUseCode] = useState(!hasPassword);
  const [value, setValue] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const sendCode = async () => {
    setBusy(true);
    try { await http.post("/account/reauth/code"); setSent(true); toast.success(`Code sent to ${email}`); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/account/reauth", useCode ? { code: value } : { password: value });
      setValue(""); onDone(data.reauth_token);
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className={DIALOG} data-testid="reauth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-sky-300" /> Confirm it's you</DialogTitle>
          <DialogDescription className="text-neutral-400">{useCode ? `We'll email a 6-digit code to ${email}.` : "Enter your password to continue."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {useCode && !sent && <button type="button" onClick={sendCode} disabled={busy} className={BTN} data-testid="reauth-send-code-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Email me a code</button>}
          {(!useCode || sent) && (
            <Input autoFocus type={useCode ? "text" : "password"} inputMode={useCode ? "numeric" : undefined} autoComplete={useCode ? "one-time-code" : "current-password"}
              value={value} onChange={(e) => setValue(e.target.value)} placeholder={useCode ? "6-digit code" : "Your password"} maxLength={useCode ? 6 : 200}
              className="bg-white/5 border-white/10" aria-label={useCode ? "Confirmation code" : "Password"} data-testid="reauth-input" />
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {hasPassword ? (
              <button type="button" onClick={() => { setUseCode(!useCode); setValue(""); }} className="text-xs text-sky-300 hover:underline" data-testid="reauth-switch-method">
                {useCode ? "Use my password instead" : "Email me a code instead"}
              </button>
            ) : <span />}
            {(!useCode || sent) && <button type="submit" disabled={busy || !value} className={BTN} data-testid="reauth-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Continue</button>}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Sensitive account changes need a fresh confirmation; one confirmation is reused for a few minutes.
export function useReauth(account) {
  const [req, setReq] = useState(null);
  const cache = useRef({ token: null, until: 0 });
  const getToken = useCallback(() => {
    if (cache.current.token && Date.now() < cache.current.until) return Promise.resolve(cache.current.token);
    return new Promise((resolve, reject) => setReq({ resolve, reject }));
  }, []);
  const dialog = req && (
    <ConfirmIdentityDialog open email={account?.email} hasPassword={!!account?.has_password}
      onDone={(t) => { cache.current = { token: t, until: Date.now() + 9 * 60000 }; req.resolve(t); setReq(null); }}
      onCancel={() => { req.reject({ cancelled: true }); setReq(null); }} />
  );
  return { getToken, dialog };
}
